/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {ClickAwayListener, ListItem, makeStyles, Paper} from '@material-ui/core';
import MenuList from '@material-ui/core/MenuList';
import Popper from '@material-ui/core/Popper';
import {translate} from '@ion/core/src/locale/locale';
import {
  DownloadJobTasks,
  GenericObject,
  PlatformDeviceType,
  PlatformDeviceTypes,
  PlatformFileType
} from '@ion/core/src/types/entities';
import IonSpinner from '@ion/dls/src/components/loading/IonSpinner';
import TextField from '@ion/dls/src/components/textfield/TextField';
import {DynamicTreeView} from '@ion/dls/src/components/treeviews/DynamicTreeView';
import {TreeViewDataItem} from '@ion/dls/src/components/treeviews/TreeViews';
import {DropdownArrowIcon} from '@ion/dls/src/icons/DropdownArrowIcon';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {DynamicDataItem, Item} from '@ion/features/src/search/types';
import {filterDynamicDataList, transformDeviceTypesToTree} from '@ion/features/src/search/utils';
import useDeviceTypes from '@ion/query/src/hooks/useDeviceTypes';

// defines the device/file types chooser prop definitions ...
export type JobTaskDeviceFileTypeChooserProps = {
  index: number;
  tasks: DownloadJobTasks;
  taskValue: object;
  handleDeviceFileType: Function;
  deviceTypes: PlatformDeviceTypes;
  updateTaskValue: Function;
  isDevicesSelected?: boolean;
  isFetching?: boolean;
  isFormattedText?: boolean;
};

export interface JobDeviceFileTypeChooserPermissionsProps {
  Features?: GenericObject;
};

const MenuListID       = 'suggestion-menu';
const DeviceFileTypeID = 'device-file-type';

const useStyles = makeStyles((theme: DLSTheme) => ({
  searchSuggestionInputRoot : {
    height       : '48px',
    marginBottom : '10px'
  },
  searchSuggestionInputUnderline : {
    paddingBottom : '0px !important',
    height        : 'inherit',
    '&::before'   : {
      borderBottom : `1px solid ${dlsColor('grey4')} !important`
    },
    '&.Mui-focused' : {
      '&::before' : {
        borderBottom : `1px solid ${dlsColor('aaBlue')} !important`
      }
    }
  },
  suggestionsWrapper : {
    zIndex   : 5,
    position : 'relative',
    border   : '1px solid #e0e0e0',
    maxWidth : 500,
    width    : '100%',
    left     : '-6px !important'
  },
  searchIcon : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    cursor         : 'pointer'
  },
  disabledSearchIcon : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  listWrapper : {
    height    : 'auto',
    maxHeight : '280px',
    overflow  : 'scroll'
  },
  input : {
    fontSize    : 14,
    fontFamily  : theme.typography.fontFamily,
    paddingLeft : '7px'
  },
  searchResultText : {
    color      : dlsColor('grey3'),
    ...dlsAlt('typography', 'searchInput'),
    '& #match' : {
      fontWeight      : 'bold',
      backgroundColor : ({highlightColor}: {highlightColor?: string}) => highlightColor || 'transparent'
    },
    '& #associatematch' : {
      fontWeight : 'bold',
      color      : dlsColor('caBlue')
    }
  },
  emptyListWrapper : {
    height    : 'auto',
    maxHeight : '400px'
  },
  spinner : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    width          : '100%',
    height         : '39px'
  }
}));

/**
 * The JobTaskDeviceFileTypeChooser provides a list of device and file types for the current account
 * and allows the user to single-select a device and file type from the list.
 *
 * ```typescript
 *
 *     import {JobTaskDeviceFileTypeChooser} from './JobTaskDeviceFileTypeChooser';
 *
 *     <JobTaskDeviceFileTypeChooser index={index}
 *      deviceTypes={deviceTypes}
 *      tasks={tasks}
 *      taskValue={taskValue}
 *      handleDeviceType={handleDeviceType}
 *      handleFileType={handleFileType}
 *      updateTaskValue={updateTaskValue} />
 *
 * ```
 */
export const JobTaskDeviceFileTypeChooser = ({
  index,
  deviceTypes,
  tasks,
  taskValue,
  handleDeviceFileType,
  updateTaskValue,
  isFetching = false,
  isFormattedText = true,
  isDevicesSelected = false
}: JobTaskDeviceFileTypeChooserProps) => {
  const classes                                 = useStyles();
  const queryInputRef                           = useRef([]);
  const menuListRef                             = useRef([]);
  const [dropdownOpen, setDropdownOpen]         = useState<boolean>(false);
  const [textClicked, setTextClicked]           = useState<boolean>(false);
  const [noOptions, setNoOptions]               = useState<boolean>(true);
  const [searchQuery, setSearchQuery]           = useState('');
  const [searchText, getSearchText]             = useState('');
  const [filteredTreeData, setFilteredTreeData] = useState<DynamicDataItem[]>(null);

  const {isLoading}     = useDeviceTypes();
  const deviceTypesTree = useMemo(() => transformDeviceTypesToTree(deviceTypes), [deviceTypes]);

  const onTreeViewLabelSelect = useCallback(function(item: TreeViewDataItem) {
    onMenuSelection(item, index, tasks);
  }, [index, tasks]);

  const onMenuSelection = useCallback(function(
    menuItem: TreeViewDataItem,
    taskIndex: number,
    tasks
  ) {
    const {fullName, fileTypes, deviceType, platformId} = menuItem;
    updateTaskValue({
      ...taskValue,
      [taskIndex] : fullName
    });
    const deviceTypeOption = {
      name : deviceType,
      fileTypes
    } as PlatformDeviceType;
    const fileTypeOption = fileTypes.find(
      fileType => fileType.platformId === platformId
    ) as PlatformFileType;
    setDropdownOpen(false);
    setSearchQuery(fullName);
    handleDeviceFileType({
      tasks : {tasks: tasks},
      deviceTypeOption,
      fileTypeOption,
      taskIndex
    });
  }, [tasks, taskValue]);

  const tableRowsRefArray = useCallback((index, refType = 'textField') => element => {
    if (refType === 'textField') {
      queryInputRef.current[index] = element;
    } else {
      menuListRef.current[index] = element;
    }
  }, [queryInputRef, menuListRef]);

  const handleClose = useCallback((_taskIndex) => () => {
    setDropdownOpen(false);
  }, []);

  const onInputChange = useCallback(({currentTarget, target}: ChangeEvent<HTMLInputElement>) => {
    const {value} = target;
    const formattedValue = value.toLowerCase();
    const filteredData = value ? filterDynamicDataList(deviceTypesTree, value, -1, 'searchableString', isFormattedText) : null;
    const filteredResults = filteredData?.length ? [...filteredData] : null;
    const list = [];
    if (filteredData) {
      filteredResults?.forEach((item: Item) => {
        const {name, children} = item;
        if (!name.toLowerCase().includes(formattedValue) && children?.length) {
          const filteredchildren = children?.filter(({name}: Item) => name.toLowerCase().includes(formattedValue));
          if (filteredchildren?.length) {
            const n = {...item, children: [...filteredchildren]};
            list.push(n);
          }
        } else {
          list.push(item);
        }
      });
    }
    if (!list?.length && value) {
      setNoOptions(false);
    } else {
      setNoOptions(true);
    }
    if (value) {
      setTextClicked(true);
      getSearchText(value);
    } else {
      setTextClicked(false);
      getSearchText(value);
      setNoOptions(true);
    }
    setFilteredTreeData(list?.length ? list : null);
    setSearchQuery(value);
    setDropdownOpen(true);
  }, [deviceTypes]);

  useEffect(() => {
    setSearchQuery(taskValue[index]);
  }, [taskValue]);

  const onClick = useCallback(function(canSeeDropdown = true): () => void {
    return () => {
      setDropdownOpen(canSeeDropdown);
    };
  }, []);

  if (isLoading || isFetching) {
    return (
      <div className={classes.spinner}>
        <IonSpinner width='25px' height='25px' />
      </div>
    );
  }

  return (
    <ClickAwayListener onClickAway={handleClose(index)}>
      <div>
        <TextField id={`${DeviceFileTypeID}-${index}`}
          onFocus={onClick()}
          classes={{root: classes.searchSuggestionInputRoot}}
          autoComplete={'off'}
          placeholder={`${translate('jobcreate:fields.deviceFileType.placeholder')}`}
          inputRef={tableRowsRefArray(index)}
          onChange={onInputChange}
          value={searchQuery}
          aria-controls={dropdownOpen ? 'menu-list-grow' : undefined}
          aria-haspopup="true"
          disabled={!isDevicesSelected}
          InputProps={{
            classes : {
              input     : classes.input,
              underline : classes.searchSuggestionInputUnderline
            },
            endAdornment : (<div className={isDevicesSelected ? classes.searchIcon : classes.disabledSearchIcon}
              onClick={onClick(!dropdownOpen)}>
              <DropdownArrowIcon color={!isDevicesSelected ? dlsAlt('components', 'btn', 'disabledColor') : ''}/>
            </div>)
          }}/>
        <Popper open={dropdownOpen && isDevicesSelected}
          className={classes.suggestionsWrapper}
          anchorEl={dropdownOpen ? queryInputRef?.current?.[index] : null}
          role={undefined}
          transition
          disablePortal
          style={{position: 'absolute', top: 65}}>
          <Paper>
            {dropdownOpen && isDevicesSelected && (
              ((filteredTreeData ?? deviceTypesTree)?.length && noOptions) ? (
                <MenuList id={`${MenuListID}-${index}`}
                  ref={tableRowsRefArray(index, 'menuList')}
                  variant={'menu'}
                  classes={{root: classes.listWrapper}}>
                  <DynamicTreeView expandOnOptionClick
                    parentClick={false}
                    searchText={searchText}
                    textClicked={textClicked}
                    data={filteredTreeData ?? deviceTypesTree}
                    onItemClick={onTreeViewLabelSelect} />
                </MenuList>
              ) : (<MenuList id={`${MenuListID}-${index}`}
                ref={tableRowsRefArray(index, 'menuList')}
                variant={'menu'}
                classes={{root: classes.emptyListWrapper}}>
                <ListItem className={classes.searchResultText}>{translate('common:features.devicetypes.noOptionsText')}</ListItem>
              </MenuList>)
            )}
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

export default JobTaskDeviceFileTypeChooser;
